import { TEST_BUILDER_COUNT_OPTIONS } from "@/lib/test-tools/constants";

function textField(name, label, options = {}) {
  return { name, label, type: "text", ...options };
}

function textareaField(name, label, options = {}) {
  return { name, label, type: "textarea", rows: 4, ...options };
}

function listField(name, label, options = {}) {
  return { name, label, type: "list", rows: 4, ...options };
}

function normalizeString(value) {
  return typeof value === "string" ? value.trim() : "";
}

export const AI_TEST_CONTENT_TYPE_ORDER = [
  "multiple-choice-tests",
  "faq-tests",
  "glossary-tests",
];

export const AI_TEST_CONTENT_TYPES = {
  "multiple-choice-tests": {
    id: "multiple-choice-tests",
    label: "Multiple choice tests",
    singularLabel: "multiple choice test",
    description:
      "Create multiple choice test drafts from studio context and attached source material.",
    domain: "tests",
    generateTool: "generateMultipleChoiceTestCandidates",
    saveTool: "saveGeneratedMultipleChoiceTests",
    countOptions: TEST_BUILDER_COUNT_OPTIONS,
    defaultCount: 1,
    countParam: "count",
    generateDefaults: {
      questionsPerTest: 5,
    },
    generatedKeys: ["tests"],
    savedKeys: ["createdTests"],
    adminPath: "/workspaces/admin/tests",
    buildSaveParams: ({ candidates, selectedIds, selectedItems, workspaceId }) => ({
      workspaceId,
      generatedTests: candidates,
      selectedTestIds: selectedIds,
      selectedTests: selectedItems,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.name) || `Multiple choice test ${index + 1}`,
    fields: [
      textField("name", "Name"),
      textareaField("description", "Description"),
      textField("questionCount", "Question count", { readOnly: true }),
      listField("questionPreview", "Questions", {
        rows: 8,
        readOnly: true,
        helpText: "Preview of the question stems included in this test draft.",
      }),
    ],
  },
  "faq-tests": {
    id: "faq-tests",
    label: "Q&A tests",
    singularLabel: "Q&A test",
    description:
      "Create Q&A test drafts from the Q&As already saved in your studio.",
    domain: "tests",
    generateTool: "generateFAQTestCandidates",
    saveTool: "saveGeneratedFAQTests",
    countOptions: TEST_BUILDER_COUNT_OPTIONS,
    defaultCount: 1,
    countParam: "count",
    // Let the generator choose its default from the available Q&As. An
    // explicit itemsPerTest/questionCount is a strict caller requirement.
    generatedKeys: ["tests"],
    savedKeys: ["createdTests"],
    adminPath: "/workspaces/admin/faq-tests",
    buildSaveParams: ({ candidates, selectedIds, selectedItems }) => ({
      generatedTests: candidates,
      selectedTestIds: selectedIds,
      selectedTests: selectedItems,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.name) || `Q&A test ${index + 1}`,
    fields: [
      textField("name", "Name"),
      textareaField("description", "Description"),
      textField("questionCount", "Included Q&As", { readOnly: true }),
      listField("faqPreview", "Q&A preview", {
        rows: 8,
        readOnly: true,
        helpText: "Questions that will be included in this Q&A test draft.",
      }),
    ],
  },
  "glossary-tests": {
    id: "glossary-tests",
    label: "Glossary tests",
    singularLabel: "glossary test",
    description:
      "Create glossary test drafts from the glossary entries already saved in your studio.",
    domain: "tests",
    generateTool: "generateGlossaryTestCandidates",
    saveTool: "saveGeneratedGlossaryTests",
    countOptions: TEST_BUILDER_COUNT_OPTIONS,
    defaultCount: 1,
    countParam: "count",
    generateDefaults: {
      itemsPerTest: 8,
    },
    generatedKeys: ["tests"],
    savedKeys: ["createdTests"],
    adminPath: "/workspaces/admin/glossary-tests",
    buildSaveParams: ({ candidates, selectedIds, selectedItems }) => ({
      generatedTests: candidates,
      selectedTestIds: selectedIds,
      selectedTests: selectedItems,
    }),
    getItemTitle: (item, index = 0) =>
      normalizeString(item?.name) || `Glossary test ${index + 1}`,
    fields: [
      textField("name", "Name"),
      textareaField("description", "Description"),
      textField("questionCount", "Included terms", { readOnly: true }),
      listField("glossaryPreview", "Glossary preview", {
        rows: 8,
        readOnly: true,
        helpText: "Terms that will be included in this glossary test draft.",
      }),
    ],
  },
};
